import { useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";

/**
 * ReferralRedirect — resolves `/r/{code}` (and optional `?to=path`) into
 * the correct landing surface with the referral code preserved as a
 * `?ref={code}` query so the site-wide `UtmTracker` component picks it
 * up, stashes it in `localStorage.cauldron_ref` (30-day TTL), and fires
 * the `/api/analytics/utm-hit` ping for attribution.
 *
 * Supported target aliases (safe subset of public routes only — we do
 * NOT allow arbitrary redirects to prevent open-redirect abuse):
 *   - (default)   → /            (Landing / Home)
 *   - clients     → /for/clients
 *   - elders      → /elders
 *   - ambassadors → /ambassadors
 *   - kit         → /ambassadors/kit
 *   - apply       → /ambassadors/apply
 *   - <slug>      → /for/<slug>  (any of the 17 niches)
 */
const NICHE_SLUGS = new Set([
  "reiki-masters", "tarot-readers", "psychic-mediums", "astrologers",
  "hoodoo-practitioners", "conjure-workers", "babalawos", "santeros",
  "houngans-mambos", "ceremonialists", "energy-healers", "crystal-healers",
  "herbalists", "shamans", "witches", "mediums", "numerologists",
]);

const ALIASES = {
  clients: "/for/clients",
  elders: "/elders",
  ambassadors: "/ambassadors",
  kit: "/ambassadors/kit",
  apply: "/ambassadors/apply",
  home: "/",
};

function resolveTarget(to) {
  if (!to) return "/";
  const clean = String(to).trim().toLowerCase();
  if (ALIASES[clean]) return ALIASES[clean];
  if (NICHE_SLUGS.has(clean)) return `/for/${clean}`;
  return "/"; // unknown alias → fall back to home
}

export default function ReferralRedirect() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();

  useEffect(() => {
    // Stash the referral code synchronously BEFORE navigating so downstream
    // landing pages that strip search params (e.g. Elders, filtered
    // marketplaces) can't wipe the attribution before UtmTracker runs.
    if (code) {
      try {
        localStorage.setItem("cauldron_ref", code);
        localStorage.setItem(
          "cauldron_ref_expires",
          String(Date.now() + 30 * 24 * 60 * 60 * 1000),
        );
      } catch { /* private-mode / storage-full — non-fatal */ }
    }
    const target = resolveTarget(params.get("to"));
    // Pass every incoming query param through so campaigns can add
    // utm_source, utm_medium, etc. and still land intact.
    const outParams = new URLSearchParams(params);
    outParams.delete("to");
    if (code) outParams.set("ref", code);
    const qs = outParams.toString();
    nav(`${target}${qs ? `?${qs}` : ""}`, { replace: true });
  }, [code, params, nav]);

  // Render nothing — this is a pure redirect
  return null;
}
